/**
 * Procedural Animation Generator for walking, breathing, gestures, waving, and blackboard writing.
 */
import { CharacterRig, BoneAngles } from './CharacterRig';

export class CharacterAnimator {
  private rig: CharacterRig;
  private walkTime: number = 0;
  private breatheTime: number = 0;
  private blinkTimer: number = 0;

  constructor(rig: CharacterRig) {
    this.rig = rig;
  }

  public updateIdle(deltaSec: number): void {
    this.breatheTime += deltaSec * 2.5;
    this.blinkTimer += deltaSec;

    // Subtle spine breathing
    const breath = 1 + Math.sin(this.breatheTime) * 0.015;
    const headTilt = Math.sin(this.breatheTime * 0.5) * 1.5;

    // Periodic automatic blinking every 3-5 seconds
    if (this.blinkTimer > 3.5) {
      if (this.blinkTimer < 3.7) {
        // Fast blink
      } else {
        this.blinkTimer = 0;
      }
    }

    this.rig.setBoneAngles({
      bodyTilt: 0,
      headAngle: headTilt,
      breathingScaleY: breath,
      leftUpperArm: 12 + Math.sin(this.breatheTime) * 3,
      leftLowerArm: 8,
      rightUpperArm: -12 - Math.sin(this.breatheTime) * 3,
      rightLowerArm: -8,
      leftUpperLeg: 0,
      leftLowerLeg: 0,
      rightUpperLeg: 0,
      rightLowerLeg: 0
    });
  }

  public animateWalk(speed: number, deltaSec: number): void {
    this.walkTime += deltaSec * speed * 6;
    const stride = 28; // max leg angle

    const lHip = Math.sin(this.walkTime) * stride;
    const rHip = -Math.sin(this.walkTime) * stride;

    // Natural knee bending on forward swing
    const lKnee = lHip > 0 ? Math.sin(this.walkTime) * 35 : 0;
    const rKnee = rHip > 0 ? -Math.sin(this.walkTime) * 35 : 0;

    // Synchronized opposing arm swings
    const lArm = -rHip * 0.8;
    const rArm = -lHip * 0.8;

    const bodyBounce = Math.abs(Math.sin(this.walkTime * 2)) * 3;

    this.rig.setBoneAngles({
      bodyTilt: 2,
      headAngle: -Math.sin(this.walkTime) * 2,
      breathingScaleY: 1,
      leftUpperArm: 10 + lArm,
      leftLowerArm: 15,
      rightUpperArm: -10 + rArm,
      rightLowerArm: -15,
      leftUpperLeg: lHip,
      leftLowerLeg: lKnee,
      rightUpperLeg: rHip,
      rightLowerLeg: rKnee
    });
  }

  public animateWave(deltaSec: number): void {
    this.walkTime += deltaSec * 8;
    const wave = Math.sin(this.walkTime) * 30;

    this.rig.setBoneAngles({
      bodyTilt: 0,
      headAngle: 4,
      rightUpperArm: -130, // raised arm
      rightLowerArm: -40 + wave, // waving forearm
      leftUpperArm: 15,
      leftLowerArm: 10
    });
  }

  public animatePoint(pointX: number, pointY: number): void {
    // Pointing right arm towards target
    this.rig.setBoneAngles({
      bodyTilt: 2,
      headAngle: -6,
      rightUpperArm: -85,
      rightLowerArm: -10,
      leftUpperArm: 15,
      leftLowerArm: 10
    });
  }

  public animateWrite(deltaSec: number): void {
    this.walkTime += deltaSec * 6;
    const chalkMove = Math.sin(this.walkTime) * 8;

    this.rig.setBoneAngles({
      bodyTilt: 3,
      headAngle: 8,
      rightUpperArm: -75,
      rightLowerArm: -25 + chalkMove,
      leftUpperArm: 20,
      leftLowerArm: 15
    });
  }

  public animateClap(deltaSec: number): void {
    this.walkTime += deltaSec * 10;
    const clap = Math.sin(this.walkTime) * 25;

    this.rig.setBoneAngles({
      bodyTilt: 0,
      headAngle: 0,
      leftUpperArm: 50 + clap,
      leftLowerArm: 60,
      rightUpperArm: -50 - clap,
      rightLowerArm: -60
    });
  }
}
